import { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Transaction } from '../../../services/types';
import OpenOnPhone from '../../../components/OpenOnPhone/OpenOnPhone';
import UploadNotice from '../../../components/UploadNotice/UploadNotice';
import { useReceiptPicker } from '../../../hooks/useReceiptPicker';
import {
  uploadErrorTextKey,
  uploadReceipt as sendReceipt,
} from '../../../services/receiptUpload';
import {
  GateScreen,
  YellowCard,
  GateSpinnerRing,
  GatePrimaryButton,
  GateUploadBtn,
  LION_GREEN_LIGHT,
} from '../components';

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── SVG icons ────────────────────────────────────────────────────────────────
const RemoveIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
    style={{ cursor: 'pointer', borderRadius: '25%', backgroundColor: 'inherit', transition: '0.5s', flexShrink: 0 }}>
    <path d="M17.6598 17.6598L6.33984 6.33984" stroke="#D63224" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.34016 17.6598L17.6602 6.33984" stroke="#D63224" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SendIcon = () => (
  <svg width="25" height="25" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.51 4.23L18.07 8.51C22.91 10.93 22.91 13.07 18.07 15.49L9.51 19.77C2.49 23.28 -0.17 20.62 3.34 13.6L4.21 11.84C4.44 11.39 4.44 10.62 4.21 10.17L3.34 8.4C-0.17 1.38 2.5 -1.28 9.51 4.23Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M4.44 12H10.84" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ─── Styled ───────────────────────────────────────────────────────────────────
const WarningIcon = styled.div`
  font-size: 36px;
  text-align: center;
  margin-bottom: 8px;
`;

const WarningTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  text-align: center;
  color: #333;
  font-family: 'PT Mono', monospace;
  margin-bottom: 6px;
`;

const WarningText = styled.div`
  font-size: 12px;
  color: #777;
  text-align: center;
  font-family: 'PT Mono', monospace;
  line-height: 1.5;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  overflow: hidden;
  padding: 8px 24px;
  margin-bottom: 16px;
  background-color: ${LION_GREEN_LIGHT};
  border-radius: 16px;
  box-sizing: border-box;
  animation: ${fadeIn} 0.2s ease;
`;

const FileName = styled.p`
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  color: rgb(80, 149, 56);
  margin: 0;
  word-break: break-all;
`;

const RemoveBtnWrap = styled.div`
  flex-shrink: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const SendBtn = styled(GatePrimaryButton)`
  margin-bottom: 16px;
`;

const SendBtnIconWrap = styled.span`
  display: inherit;
  margin-right: 8px;
  margin-left: -4px;
`;

const SendBtnText = styled.span``;

// ─── Component ────────────────────────────────────────────────────────────────
interface Props {
  transaction: Transaction;
  uuid: string;
  timeLeft: string;
  onConfirm: (file?: any) => Promise<void>;
  onBack: () => void;
  // CPAYMENT-1616: re-upload after a failed auto-verification — show a neutral retry message.
  reupload?: boolean;
}

const GatePercent = styled.span`
  font-size: 11px;
  color: rgb(92, 138, 30);
  font-family: 'PT Mono', monospace;
  margin-top: 4px;
  display: block;
  text-align: center;
`;

const GateUploadPage = ({ transaction, uuid, timeLeft, onConfirm, onBack, reupload = false }: Props) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [retryNotice, setRetryNotice] = useState('');
  // CPAYMENT-1822: the press, the cancel, the empty pick and the silence after it are all handled
  // in one place now — see hooks/useReceiptPicker.
  const picker = useReceiptPicker({
    uuid,
    transaction,
    onFile: setFile,
    onError: (key) => setErrorText(t(key)),
    onBusy: setSending,
    onPickStart: () => {
      setErrorText('');
      setRetryNotice('');
    },
  });

  // CPAYMENT-1786: the re-upload path uses the same service as the main screen, so a failure here
  // is classified, reported and retried exactly like the first attempt instead of silently
  // collapsing into one error string.
  const handleSend = async () => {
    if (!file || sending) return;
    setSending(true);
    setErrorText('');
    setRetryNotice('');
    setUploadProgress(0);
    try {
      const result = await sendReceipt(uuid, transaction, file, {
        // This screen only ever replaces a receipt the auto-check rejected, so a 409 here means the
        // backend refused the replacement — not that the file is safely stored.
        isReplacement: true,
        onProgress: setUploadProgress,
        onRetryScheduled: () => setRetryNotice(t('uploadStatus.retrying')),
      });
      setRetryNotice('');

      if (result.ok) {
        await onConfirm();
        return;
      }

      setUploadProgress(0);
      setErrorText(t(uploadErrorTextKey(result.reason, result.httpStatus)));
    } finally {
      setSending(false);
    }
  };

  return (
    <GateScreen
      uuid={uuid}
      timeLeft={timeLeft}
      actions={[
        { label: t('common.back'), onClick: onBack, variant: 'grey', dataSentryElement: 'onepage-reupload-back' },
      ]}
      // CPAYMENT-1718: под загрузчиком, над «Назад». Во время отправки прячем.
      footerSlot={
        sending ? undefined : (
          <OpenOnPhone
            variant='lion'
            orLabel={t('common.or')}
            title={t('upload.openOnPhone')}
            hint={t('upload.openOnPhoneHint')}
          />
        )
      }
    >
      <YellowCard>
        <WarningIcon>⚠️</WarningIcon>
        {reupload ? (
          <WarningTitle>{t('verification.reuploadFailed')}</WarningTitle>
        ) : (
          <>
            <WarningTitle>
              {t('upload.required')}<br />
              {t('upload.requiredSub')}
            </WarningTitle>
            <WarningText>{t('upload.instructionsPdfOrImage')}</WarningText>
          </>
        )}
      </YellowCard>

      {file && (
        <FileRow>
          <FileName>{file.name}</FileName>
          <RemoveBtnWrap data-sentry-element='onepage-reupload-file-remove' onClick={() => setFile(null)}>
            <RemoveIcon />
          </RemoveBtnWrap>
        </FileRow>
      )}

      {errorText && <UploadNotice tone='error'>{errorText}</UploadNotice>}

      {/* CPAYMENT-1786: a retry in flight is neutral — nothing for the client to fix, only to wait. */}
      {retryNotice && !errorText && <UploadNotice tone='neutral'>{retryNotice}</UploadNotice>}

      {file ? (
        <SendBtn data-sentry-element='onepage-reupload-send' onClick={handleSend} disabled={sending}>
          {sending ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <GateSpinnerRing size={28}>
                <span style={{ fontSize: 8, color: '#fff' }}>●</span>
              </GateSpinnerRing>
              {uploadProgress > 0 && <GatePercent>{uploadProgress}%</GatePercent>}
            </div>
          ) : (
            <>
              <SendBtnIconWrap><SendIcon /></SendBtnIconWrap>
              <SendBtnText>{t('upload.sendBtn')}</SendBtnText>
            </>
          )}
        </SendBtn>
      ) : (
        <GateUploadBtn label={t('upload.uploadBtn')} onClick={picker.open} dataSentryElement='onepage-reupload-pick' />
      )}
      {/* CPAYMENT-1822: two presses that produced no file. Whether the dialog failed to open or the
          client left it empty-handed, the page used to say nothing and he just kept tapping. */}
      {picker.helpVisible && <UploadNotice tone='neutral'>{t('upload.pickerHelp')}</UploadNotice>}
    </GateScreen>
  );
};

export default GateUploadPage;
